import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Team } from '../types/api';
import { apiService } from '../services/api';
import i18n from '../i18n';
import toast from 'react-hot-toast';

interface TeamContextType {
  currentTeam: Team | null;
  availableTeams: Team[];
  loading: boolean;
  error: string | null;
  setCurrentTeam: (team: Team) => void;
  setCurrentTeamBySlug: (slug: string) => Promise<void>;
  setCurrentTeamByPublicId: (publicId: string, slug: string) => Promise<void>;
  switchTeam: (teamId: number) => void;
  refreshTeams: () => Promise<void>;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

interface TeamProviderProps {
  children: ReactNode;
}

export const TeamProvider: React.FC<TeamProviderProps> = ({ children }) => {
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load all teams on mount
  const refreshTeams = async () => {
    try {
      setLoading(true);
      setError(null);
      const teams = await apiService.getTeams();
      setAvailableTeams(teams);
      
      // Set current team from localStorage or first available team
      const savedTeamId = localStorage.getItem('currentTeamId');
      if (savedTeamId) {
        const savedTeam = teams.find(t => t.teamId === parseInt(savedTeamId));
        if (savedTeam) {
          setCurrentTeam(savedTeam);
        } else if (teams.length > 0) {
          setCurrentTeam(teams[0]);
          localStorage.setItem('currentTeamId', teams[0].teamId.toString());
        }
      } else if (teams.length > 0) {
        setCurrentTeam(teams[0]);
        localStorage.setItem('currentTeamId', teams[0].teamId.toString());
      }
    } catch (err: any) {
      console.error('Error loading teams:', err);
      setError(i18n.t('settings:errors.loadTeams'));
      // Only show toast if it's not an authentication error
      // Authentication errors are expected when not logged in and are silently handled
      const isAuthError = err?.message?.includes('Authentication failed') || err?.message?.includes('401');
      if (!isAuthError) {
        toast.error(i18n.t('settings:errors.loadTeams'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Set current team by slug
  const setCurrentTeamBySlug = async (slug: string) => {
    try {
      setLoading(true);
      setError(null);
      const team = await apiService.getTeamBySlug(slug);
      setCurrentTeam(team);
      
      // Also add to available teams if not already there
      setAvailableTeams(prev => {
        const exists = prev.some(t => t.teamId === team.teamId);
        if (!exists) {
          return [...prev, team];
        }
        return prev;
      });
    } catch (err) {
      console.error('Error loading team by slug:', err);
      setError(i18n.t('settings:errors.teamNotFound', { slug }));
      toast.error(i18n.t('settings:errors.teamNotFound', { slug }));
      throw err; // Re-throw so components can handle it
    } finally {
      setLoading(false);
    }
  };

  // Set current team by publicId and slug (secure format)
  const setCurrentTeamByPublicId = async (publicId: string, slug: string) => {
    try {
      setLoading(true);
      setError(null);
      const team = await apiService.getTeamByPublicId(publicId, slug);
      setCurrentTeam(team);
      
      // Also add to available teams if not already there
      setAvailableTeams(prev => {
        const exists = prev.some(t => t.teamId === team.teamId);
        if (!exists) {
          return [...prev, team];
        }
        return prev;
      });
    } catch (err) {
      console.error('Error loading team by publicId:', err);
      setError(i18n.t('settings:errors.teamNotFound', { slug: `${publicId}/${slug}` }));
      toast.error(i18n.t('settings:errors.teamNotFoundShort'));
      throw err; // Re-throw so components can handle it
    } finally {
      setLoading(false);
    }
  };

  // Switch team by ID
  const switchTeam = (teamId: number) => {
    const team = availableTeams.find(t => t.teamId === teamId);
    if (team) {
      setCurrentTeam(team);
      localStorage.setItem('currentTeamId', teamId.toString());
    } else {
      console.error('Team not found:', teamId);
    }
  };

  // Listen for team switches from AuthContext
  useEffect(() => {
    const handleTeamSwitch = (event: CustomEvent) => {
      const { team } = event.detail;
      setCurrentTeam(team);
    };

    // Listen for team switch events
    window.addEventListener('team-switched', handleTeamSwitch as EventListener);

    return () => {
      window.removeEventListener('team-switched', handleTeamSwitch as EventListener);
    };
  }, []);

  // Also sync from localStorage on initialization if we have availableTeams
  useEffect(() => {
    if (availableTeams.length > 0 && !currentTeam) {
      const savedTeamId = localStorage.getItem('currentTeamId');
      if (savedTeamId) {
        const savedTeam = availableTeams.find(t => t.teamId === parseInt(savedTeamId));
        if (savedTeam) {
          setCurrentTeam(savedTeam);
        }
      }
    }
  }, [availableTeams, currentTeam]);

  useEffect(() => {
    // Don't auto-load teams on mount
    // Teams will be loaded by AuthContext after successful login
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: TeamContextType = {
    currentTeam,
    availableTeams,
    loading,
    error,
    setCurrentTeam,
    setCurrentTeamBySlug,
    setCurrentTeamByPublicId,
    switchTeam,
    refreshTeams,
  };

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = (): TeamContextType => {
  const context = useContext(TeamContext);
  if (context === undefined) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};